import React, { useState } from 'react';
import { Search, Plus, Edit, Trash2, FolderTree, Image as ImageIcon, ChevronLeft, ChevronRight, Download, MoreVertical, Eye, CheckCircle, XCircle, AlertCircle, Star, Package } from 'lucide-react';
import { Link } from 'react-router-dom';

const Categories = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Static Mock Data
  const categories = [
    { id: 1, name: 'Indoor Plants', slug: 'indoor-plants', productsCount: 156, status: 'Active', popular: true, image: '🪴', description: 'Perfect for homes and offices' },
    { id: 2, name: 'Outdoor Plants', slug: 'outdoor-plants', productsCount: 243, status: 'Active', popular: true, image: '🌳', description: 'Garden and landscape plants' },
    { id: 3, name: 'Air Purifying', slug: 'air-purifying', productsCount: 89, status: 'Active', popular: false, image: '🍃', description: 'Plants that clean the air' },
    { id: 4, name: 'Medicinal Plants', slug: 'medicinal-plants', productsCount: 45, status: 'Active', popular: false, image: '🌿', description: 'Herbal and medicinal plants' },
    { id: 5, name: 'Flowering Plants', slug: 'flowering-plants', productsCount: 112, status: 'Active', popular: true, image: '🌸', description: 'Beautiful flowering varieties' },
    { id: 6, name: 'Succulents & Cacti', slug: 'succulents-cacti', productsCount: 78, status: 'Active', popular: false, image: '🌵', description: 'Low maintenance plants' },
    { id: 7, name: 'Bonsai', slug: 'bonsai', productsCount: 23, status: 'Inactive', popular: false, image: '🎍', description: 'Miniature trees' },
  ];

  const stats = {
    total: categories.length,
    active: categories.filter(c => c.status === 'Active').length,
    inactive: categories.filter(c => c.status === 'Inactive').length,
    popular: categories.filter(c => c.popular).length,
    totalProducts: categories.reduce((sum, c) => sum + c.productsCount, 0)
  };

  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          category.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || category.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const paginatedCategories = filteredCategories.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    parent: '',
    description: '',
    status: true,
    image: null
  });

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      parent: '',
      description: category.description || '',
      status: category.status === 'Active',
      image: null
    });
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      parent: '',
      description: '',
      status: true,
      image: null
    });
  };

  return (
    <div className="admin-categories-modern">
      {/* Header */}
      <div className="categories-header">
        <div>
          <h1 className="page-title">Categories</h1>
          <p className="page-subtitle">Manage product categories and taxonomy</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green">
            <FolderTree size={20} />
          </div>
          <div>
            <h3>{stats.total}</h3>
            <span>Total Categories</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">
            <CheckCircle size={20} />
          </div>
          <div>
            <h3>{stats.active}</h3>
            <span>Active</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <Star size={20} />
          </div>
          <div>
            <h3>{stats.popular}</h3>
            <span>Popular</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <Package size={20} />
          </div>
          <div>
            <h3>{stats.totalProducts}</h3>
            <span>Total Products</span>
          </div>
        </div>
      </div>

      <div className="content-grid">
        {/* Categories List */}
        <div className="list-container">
          {/* Filters */}
          <div className="filters-bar">
            <div className="search-box">
              <Search size={18} />
              <input 
                type="text" 
                placeholder="Search by name or slug..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="status-filters">
              <button className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All</button>
              <button className={`filter-btn ${filterStatus === 'Active' ? 'active' : ''}`} onClick={() => setFilterStatus('Active')}>Active</button>
              <button className={`filter-btn ${filterStatus === 'Inactive' ? 'active' : ''}`} onClick={() => setFilterStatus('Inactive')}>Inactive</button>
            </div>
          </div>

          {/* Categories Table */}
          <div className="table-container">
            <table className="categories-table">
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>Slug</th>
                  <th>Products</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCategories.map((category) => (
                  <tr key={category.id}>
                    <td className="category-cell">
                      <div className="category-icon">
                        <span>{category.image}</span>
                      </div>
                      <div className="category-info">
                        <div className="category-name">
                          {category.name}
                          {category.popular && <span className="popular-badge">Popular</span>}
                        </div>
                        <div className="category-desc">{category.description}</div>
                      </div>
                    </td>
                    <td className="slug-cell">{category.slug}</td>
                    <td className="products-cell">{category.productsCount} items</td>
                    <td className="status-cell">
                      <span className={`status-badge ${category.status === 'Active' ? 'active' : 'inactive'}`}>
                        {category.status === 'Active' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        {category.status}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="action-buttons">
                        <button className="action-btn edit" onClick={() => handleEdit(category)} title="Edit">
                          <Edit size={16} />
                        </button>
                        <button className="action-btn delete" title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="pagination-wrapper">
              <div className="pagination-info">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredCategories.length)} of {filteredCategories.length} categories
              </div>
              <div className="pagination-buttons">
                <button 
                  className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  let pageNum = i + 1;
                  if (totalPages > 5 && currentPage > 3) {
                    pageNum = currentPage - 3 + i;
                    if (pageNum > totalPages) return null;
                  }
                  return (
                    <button 
                      key={i}
                      className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && currentPage < totalPages - 2 && <span className="page-dots">...</span>}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <button className="page-number" onClick={() => setCurrentPage(totalPages)}>
                    {totalPages}
                  </button>
                )}
                <button 
                  className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        <div className="form-container">
          <div className="form-card">
            <div className="form-header">
              {editingCategory ? (
                <>
                  <Edit size={18} />
                  <h5>Edit Category</h5>
                </>
              ) : (
                <>
                  <Plus size={18} />
                  <h5>Add New Category</h5>
                </>
              )}
            </div>
            <div className="form-body">
              <div className="form-group">
                <label>Category Name</label>
                <input 
                  type="text" 
                  className="form-input"
                  placeholder="e.g., Indoor Plants" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Slug</label>
                <input 
                  type="text" 
                  className="form-input"
                  placeholder="e.g., indoor-plants" 
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                />
                <div className="form-hint">Friendly URL string based on name</div>
              </div>
              <div className="form-group">
                <label>Parent Category</label>
                <select className="form-select">
                  <option value="">None (Top Level)</option>
                  <option value="1">Indoor Plants</option>
                  <option value="2">Outdoor Plants</option>
                  <option value="3">Flowering Plants</option>
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  className="form-textarea"
                  rows="3"
                  placeholder="Brief description about this category..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Category Image</label>
                <div className="upload-area">
                  <ImageIcon size={24} />
                  <p>Click to upload image</p>
                  <span>SVG, PNG, JPG up to 2MB</span>
                </div>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={formData.status} onChange={(e) => setFormData({...formData, status: e.target.checked})} />
                  <span>Active on website</span>
                </label>
              </div>
              <div className="form-actions">
                {editingCategory && (
                  <button type="button" className="btn-cancel" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                )}
                <button type="button" className="btn-save">
                  {editingCategory ? 'Update Category' : 'Save Category'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default Categories;